import { useStore } from '@/features/stores/StoreContext'
import { BaseLayout } from '@/components/layout/BaseLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Store, Users, Package, ShoppingCart, TrendingUp, TrendingDown, DollarSign } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/features/auth/AuthContext'
import { supabase } from '@/lib/supabase/client'
import { useQuery } from '@tanstack/react-query'

export function DashboardPage() {
  const { signOut } = useAuth()
  const { currentStore, stores, loading, createStore } = useStore()
  const [storeName, setStoreName] = useState('')
  const [creating, setCreating] = useState(false)

  const handleCreateStore = async () => {
    if (!storeName.trim()) {
      toast.error('Please enter a store name')
      return
    }
    setCreating(true)
    const { error } = await createStore(storeName)
    setCreating(false)
    if (error) {
      toast.error(error.message)
    } else {
      setStoreName('')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!currentStore) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <Store className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Create Your First Store</h2>
          <p className="text-muted-foreground mb-6">
            Get started by creating your first store.
          </p>
          <div className="space-y-3">
            <Input
              placeholder="Enter store name"
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateStore()}
            />
            <Button onClick={handleCreateStore} disabled={creating} className="w-full">
              {creating ? 'Creating...' : 'Create Store'}
            </Button>
            <Button onClick={signOut} variant="outline" className="w-full">
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <BaseLayout>
      <div className="p-8">
        {/* Store Info */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-2">{currentStore.name}</h2>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="capitalize">Plan: {currentStore.plan}</span>
            <span>•</span>
            <span className="capitalize">Status: {currentStore.status}</span>
            {stores.length > 1 && (
              <>
                <span>•</span>
                <span>{stores.length} stores total</span>
              </>
            )}
          </div>
        </div>

        <DashboardStats storeId={currentStore.id} />

        {/* Getting Started */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">🚀 Getting Started</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                1
              </div>
              <div>
                <h3 className="font-medium">Add your first product</h3>
                <p className="text-sm text-muted-foreground">
                  Start by adding products to your store catalog
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                2
              </div>
              <div>
                <h3 className="font-medium">Configure your store settings</h3>
                <p className="text-sm text-muted-foreground">
                  Set up your store name, currency, and other preferences
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                3
              </div>
              <div>
                <h3 className="font-medium">Customize your homepage</h3>
                <p className="text-sm text-muted-foreground">
                  Use the homepage builder to create your storefront
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BaseLayout>
  )
}


function DashboardStats({ storeId }: { storeId: string }) {
  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats', storeId],
    queryFn: async () => {
      const [products, orders, categories] = await Promise.all([
        supabase.from('products').select('id, price, stock, status', { count: 'exact' }).eq('store_id', storeId),
        supabase.from('orders').select('id, total', { count: 'exact' }).eq('store_id', storeId),
        supabase.from('categories').select('id', { count: 'exact' }).eq('store_id', storeId),
      ])

      const totalRevenue = orders.data?.reduce((sum, o) => sum + (o.total || 0), 0) || 0
      const totalProducts = products.count || 0
      const activeProducts = products.data?.filter(p => p.status === 'active').length || 0
      const lowStock = products.data?.filter(p => p.stock < 10).length || 0

      return {
        products: totalProducts,
        activeProducts,
        lowStock,
        orders: orders.count || 0,
        revenue: totalRevenue,
        categories: categories.count || 0,
      }
    },
    enabled: !!storeId,
  })

  return (
    <div className="grid gap-6 md:grid-cols-4 mb-8">
      <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-muted-foreground">Products</h3>
          <Package className="h-5 w-5 text-blue-500" />
        </div>
        <p className="text-3xl font-bold">{stats?.products || 0}</p>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-green-600">{stats?.activeProducts || 0} active</span>
          {(stats?.lowStock || 0) > 0 && (
            <span className="text-xs text-red-600">• {stats?.lowStock} low stock</span>
          )}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-muted-foreground">Revenue</h3>
          <DollarSign className="h-5 w-5 text-green-500" />
        </div>
        <p className="text-3xl font-bold">${(stats?.revenue || 0).toFixed(2)}</p>
        <p className="text-xs text-muted-foreground mt-2">Total earnings</p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-muted-foreground">Orders</h3>
          <ShoppingCart className="h-5 w-5 text-purple-500" />
        </div>
        <p className="text-3xl font-bold">{stats?.orders || 0}</p>
        <p className="text-xs text-muted-foreground mt-2">Total orders</p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow border-l-4 border-orange-500">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-muted-foreground">Categories</h3>
          <Store className="h-5 w-5 text-orange-500" />
        </div>
        <p className="text-3xl font-bold">{stats?.categories || 0}</p>
        <p className="text-xs text-muted-foreground mt-2">Product categories</p>
      </div>
    </div>
  )
}
